// MSAL Browser configuration. Authorization Code + PKCE against the SSO
// tenant. No client_secret, no implicit flow, no callback on the server.
// The SPA is a public client and stores tokens in sessionStorage to be
// wiped on tab close (defense-in-depth against XSS-driven token theft).

import {
  PublicClientApplication,
  type Configuration,
  type AccountInfo,
  EventType,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { config } from "../config";

const msalConfig: Configuration = {
  auth: {
    clientId: config.ssoClientId,
    authority: config.ssoAuthority,
    knownAuthorities: config.ssoKnownAuthorities,
    redirectUri: window.location.origin + "/auth/callback",
    postLogoutRedirectUri: window.location.origin + "/auth/logout",
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
    iframeHashTimeout: 10000,
    windowHashTimeout: 60000,
    loggerOptions: {
      logLevel: 3,
      piiLoggingEnabled: false,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
  scopes: ["openid", "profile", "offline_access", ...config.ssoScopes],
};

export const apiTokenRequest = {
  scopes: config.ssoScopes,
};

let activeAccount: AccountInfo | null = null;

// MSAL allows a single interactive request (redirect/popup) at a time and
// throws interaction_in_progress otherwise. Several triggers can race each
// other -- parallel API calls both hitting a 401, the session watcher and a
// user click -- so every interactive redirect is funneled through this
// single-flight gate.
let redirectInFlight = false;

const INTERACTION_STATUS_KEY = "msal.interaction.status";

async function startInteractiveRedirect(
  start: () => Promise<void>,
): Promise<void> {
  if (redirectInFlight) return;

  // Stale lock: a redirect interrupted halfway (back button from the SSO
  // page, tab restored from bfcache) leaves msal.interaction.status behind
  // and every later interactive call would throw. sessionStorage is per-tab
  // and our in-flight flag says nothing is running, so the leftover is safe
  // to drop before starting.
  sessionStorage.removeItem(INTERACTION_STATUS_KEY);

  redirectInFlight = true;
  try {
    await start();
    // Deliberately never reset on success: the page is navigating away.
  } catch (err) {
    if (
      err instanceof BrowserAuthError &&
      err.errorCode === "interaction_in_progress"
    ) {
      // Lost a race this gate couldn't see; the winning redirect is underway.
      return;
    }
    redirectInFlight = false; // real failure: allow a retry
    throw err;
  }
}

export function bootstrapMsal(): Promise<void> {
  return msalInstance
    .initialize()
    .then(() => msalInstance.handleRedirectPromise())
    .catch((err) => {
      // Stuck interaction or invalid auth response (e.g. AADSTS500011) leaves
      // msal.interaction.status set in sessionStorage. Clear it so the next
      // loginRedirect() can run instead of throwing interaction_in_progress.
      console.error("MSAL handleRedirectPromise failed:", err);
      sessionStorage.removeItem("msal.interaction.status");
    })
    .then(() => {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        activeAccount = accounts[0];
        msalInstance.setActiveAccount(activeAccount);
      }

      msalInstance.addEventCallback((event) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS &&
          event.payload &&
          "account" in event.payload &&
          event.payload.account
        ) {
          activeAccount = event.payload.account as AccountInfo;
          msalInstance.setActiveAccount(activeAccount);
        }
        if (event.eventType === EventType.LOGOUT_SUCCESS) {
          activeAccount = null;
        }
      });
    });
}

/**
 * Acquire an access token for the consent service. Tries silent acquisition
 * first (refresh token from MSAL cache); on InteractionRequiredAuthError
 * forces a full interactive redirect to the SSO -- which is the contract of
 * "session expired -> redirect to login".
 */
export async function getApiAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? activeAccount;
  if (!account) return null;

  try {
    const result = await Promise.race([
      msalInstance.acquireTokenSilent({ ...apiTokenRequest, account }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("token_acquire_timeout")), 10_000)
      ),
    ]);
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Session expired or consent revoked: bounce through the SSO.
      await startInteractiveRedirect(() =>
        msalInstance.acquireTokenRedirect({
          ...apiTokenRequest,
          account,
        }),
      );
      return null;
    }
    throw err;
  }
}

export async function login(returnTo?: string): Promise<void> {
  await startInteractiveRedirect(() =>
    msalInstance.loginRedirect({
      ...loginRequest,
      state: returnTo ?? window.location.pathname,
    }),
  );
}

export async function logout(): Promise<void> {
  const account = msalInstance.getActiveAccount() ?? activeAccount;
  await startInteractiveRedirect(() =>
    msalInstance.logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri: window.location.origin + "/auth/logout",
    }),
  );
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? activeAccount;
}
